import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../services/api';
import { FiUsers, FiShoppingBag, FiDollarSign, FiTrendingUp, FiCalendar, FiClock, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import PricingControl from './PricingControl';

interface DashboardStats {
    totalUsers: number;
    totalBookings: number;
    totalRevenue: number;
    recentBookings: any[];
}

const Dashboard: React.FC = () => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStats = async () => {
        try {
            const data = await apiFetch('/admin/stats');
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchStats();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-gold-600"></div>
            </div>
        );
    }

    return (
        <div className="p-8 min-h-screen font-sans animate-fade-in-up">
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                <div>
                    <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Admin Overview</h1>
                    <p className="text-slate-500 mt-2 font-medium">Real-time platform metrics, live bookings, and revenue tracking.</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-3 rounded-xl font-bold border border-slate-700 transition-all flex items-center gap-2 cursor-pointer shadow-md"
                        title="Refresh live metrics"
                    >
                        <FiRefreshCw className={refreshing ? 'animate-spin text-logo-gold-400' : 'text-logo-gold-400'} />
                        <span>{refreshing ? 'Refreshing...' : 'Refresh Stats'}</span>
                    </button>
                    <button className="bg-logo-gold-600 hover:bg-logo-gold-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-logo-gold-600/30 transition-all transform hover:-translate-y-0.5 flex items-center gap-2">
                        <FiTrendingUp />
                        Generate Report
                    </button>
                </div>
            </div>

            {/* Stat Cards - Premium Dark Theme */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
                
                {/* Total Users */}
                <div className="bg-slate-900 rounded-3xl p-8 relative overflow-hidden group hover:shadow-2xl hover:shadow-slate-900/20 transition-all duration-300 border border-slate-800">
                    <div className="absolute -right-6 -top-6 w-32 h-32 bg-logo-gold-500/10 rounded-full blur-2xl group-hover:bg-logo-gold-500/20 transition-all"></div>
                    <div className="flex items-center justify-between mb-6 relative z-10">
                        <div className="p-4 bg-slate-800/80 text-logo-gold-400 rounded-2xl border border-slate-700/50 backdrop-blur-sm">
                            <FiUsers size={28} />
                        </div>
                        <span className="text-sm font-bold text-green-400 flex items-center bg-green-400/10 px-3 py-1 rounded-full"><FiTrendingUp className="mr-1"/> +12%</span>
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-slate-400 font-medium mb-2 text-lg">Total Users</h3>
                        <p className="text-4xl font-extrabold text-white">{stats?.totalUsers.toLocaleString()}</p>
                    </div>
                </div>

                {/* Total Bookings */}
                <div className="bg-slate-900 rounded-3xl p-8 relative overflow-hidden group hover:shadow-2xl hover:shadow-slate-900/20 transition-all duration-300 border border-slate-800">
                    <div className="absolute -right-6 -top-6 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all"></div>
                    <div className="flex items-center justify-between mb-6 relative z-10">
                        <div className="p-4 bg-slate-800/80 text-blue-400 rounded-2xl border border-slate-700/50 backdrop-blur-sm">
                            <FiShoppingBag size={28} />
                        </div>
                        <span className="text-sm font-bold text-green-400 flex items-center bg-green-400/10 px-3 py-1 rounded-full"><FiTrendingUp className="mr-1"/> +8%</span>
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-slate-400 font-medium mb-2 text-lg">Total Bookings</h3>
                        <p className="text-4xl font-extrabold text-white">{stats?.totalBookings.toLocaleString()}</p>
                    </div>
                </div>

                {/* Revenue */}
                <div className="bg-logo-gold-600 rounded-3xl p-8 relative overflow-hidden group hover:shadow-2xl hover:shadow-logo-gold-600/30 transition-all duration-300">
                    <div className="absolute -right-6 -top-6 w-40 h-40 bg-white/20 rounded-full blur-2xl group-hover:bg-white/30 transition-all"></div>
                    <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-t from-black/20 to-transparent"></div>
                    
                    <div className="flex items-center justify-between mb-6 relative z-10">
                        <div className="p-4 bg-white/20 text-white rounded-2xl backdrop-blur-md border border-white/30">
                            <FiDollarSign size={28} />
                        </div>
                        <span className="text-sm font-bold text-white flex items-center bg-white/20 px-3 py-1 rounded-full backdrop-blur-md"><FiTrendingUp className="mr-1"/> +24%</span>
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-white/80 font-medium mb-2 text-lg">Total Revenue</h3>
                        <p className="text-4xl font-extrabold text-white">₦{stats?.totalRevenue.toLocaleString()}</p>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                
                {/* Recent Activity Table (Takes 2 columns on large screens) */}
                <div className="xl:col-span-2 bg-white rounded-3xl p-8 shadow-sm border border-slate-200">
                    <div className="flex justify-between items-center mb-8">
                        <h2 className="text-2xl font-bold text-slate-900">Recent Bookings</h2>
                        <button className="text-logo-gold-600 font-semibold hover:text-logo-gold-700 transition-colors text-sm">View All</button>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[600px]">
                            <thead>
                                <tr className="text-slate-400 text-sm border-b-2 border-slate-100">
                                    <th className="pb-4 font-bold uppercase tracking-wider">Booking ID</th>
                                    <th className="pb-4 font-bold uppercase tracking-wider">Service</th>
                                    <th className="pb-4 font-bold uppercase tracking-wider">Amount</th>
                                    <th className="pb-4 font-bold uppercase tracking-wider text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats?.recentBookings.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="py-8 text-center text-slate-500 font-medium">No recent bookings found.</td>
                                    </tr>
                                ) : (
                                    stats?.recentBookings.map((booking, index) => (
                                        <tr key={index} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/80 transition-colors group">
                                            <td className="py-5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-xs group-hover:bg-slate-200 transition-colors">
                                                        #{booking.id.substring(0,4)}
                                                    </div>
                                                    <span className="font-semibold text-slate-700">{booking.id.substring(0,8)}...</span>
                                                </div>
                                            </td>
                                            <td className="py-5">
                                                <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 capitalize border border-slate-200">
                                                    {booking.service_type}
                                                </span>
                                            </td>
                                            <td className="py-5 font-bold text-slate-900">₦{Number(booking.amount || 0).toLocaleString()}</td>
                                            <td className="py-5 text-right">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold border ${
                                                    booking.status === 'confirmed' 
                                                        ? 'bg-green-50 text-green-700 border-green-200' 
                                                        : booking.status === 'pending'
                                                        ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                                        : 'bg-red-50 text-red-700 border-red-200'
                                                } capitalize`}>
                                                    {booking.status === 'confirmed' && <FiCheckCircle className="mr-1.5" />}
                                                    {booking.status === 'pending' && <FiClock className="mr-1.5" />}
                                                    {booking.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Quick Actions / System Status */}
                <div className="bg-slate-900 rounded-3xl p-8 shadow-xl text-white relative overflow-hidden border border-slate-800">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-logo-gold-600/10 rounded-full blur-3xl"></div>
                    
                    <h2 className="text-2xl font-bold mb-8 relative z-10">System Status</h2>
                    
                    <div className="space-y-6 relative z-10">
                        <div className="p-4 rounded-2xl bg-slate-800/50 border border-slate-700 backdrop-blur-sm flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-green-500/20 text-green-400 flex items-center justify-center">
                                    <FiCheckCircle size={24} />
                                </div>
                                <div>
                                    <p className="font-bold text-white">API Server</p>
                                    <p className="text-sm text-slate-400">Operational</p>
                                </div>
                            </div>
                            <span className="w-3 h-3 rounded-full bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.5)] animate-pulse"></span>
                        </div>

                        <div className="p-4 rounded-2xl bg-slate-800/50 border border-slate-700 backdrop-blur-sm flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center">
                                    <FiCheckCircle size={24} />
                                </div>
                                <div>
                                    <p className="font-bold text-white">Database</p>
                                    <p className="text-sm text-slate-400">Connected</p>
                                </div>
                            </div>
                            <span className="w-3 h-3 rounded-full bg-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.5)]"></span>
                        </div>
                        
                        <div className="mt-8 pt-8 border-t border-slate-800">
                            <h3 className="text-lg font-bold mb-4 text-slate-300">Quick Actions</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <button className="p-4 rounded-xl bg-slate-800 hover:bg-slate-700 transition-colors text-center group border border-slate-700">
                                    <FiCalendar className="mx-auto mb-2 text-logo-gold-400 group-hover:scale-110 transition-transform" size={24} />
                                    <span className="text-sm font-semibold text-slate-300">Add Package</span>
                                </button>
                                <button className="p-4 rounded-xl bg-slate-800 hover:bg-slate-700 transition-colors text-center group border border-slate-700">
                                    <FiUsers className="mx-auto mb-2 text-blue-400 group-hover:scale-110 transition-transform" size={24} />
                                    <span className="text-sm font-semibold text-slate-300">Manage Users</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Pricing Control Section */}
            <div className="mt-8">
                <PricingControl />
            </div>
        </div>
    );
};

export default Dashboard;
